import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {useDataContextReadOnly} from "../../SurveyContextProvider";

const VARIABLE_STATE = {
    LOADING: 'variableLoading',
    BROKEN: 'variableBroken',
    OK: 'variableOk',
    MISSING: 'variableMissing'
};

const CLASS_MAP = {
    [VARIABLE_STATE.LOADING]: 'loading',
    [VARIABLE_STATE.BROKEN]: 'bad',
    [VARIABLE_STATE.OK]: 'good',
    [VARIABLE_STATE.MISSING]: 'alert'
};

const DEFAULT_MESSAGES = {
    [VARIABLE_STATE.LOADING]: 'Loading student data...',
    [VARIABLE_STATE.BROKEN]: 'Given variable does not exist in student meta.',
    [VARIABLE_STATE.OK]: 'All students have variable.',
    [VARIABLE_STATE.MISSING]: 'Some students are missing given variable.'
};

export const useVariableValidation = (variable) => {
    const intl = useIntl();
    const { value: surveyData } = useDataContextReadOnly('surveyData');
    const { value: contactsData } = useDataContextReadOnly('contactsData');
    const { value: contactMetaArray } = useDataContextReadOnly('contactMetaArray');
    const contactsLoading = contactsData == null;

    const surveyContactIds = useMemo(
        () => new Set(surveyData?.contacts),
        [surveyData?.contacts]
    );

    const contactsInSurvey = useMemo(
        () => (contactsData || []).filter(contact => surveyContactIds.has(contact.id)),
        [contactsData, surveyContactIds]
    );

    const variableIsInUse = useMemo(
        () => contactMetaArray.includes(variable),
        [contactMetaArray, variable]
    );

    const allStudentsHaveVariable = useMemo(() => {
        if (!contactsInSurvey?.length) {
            return false;
        }

        return contactsInSurvey.every(contact => {
            const metaValue = contact.meta?.[variable];
            return metaValue != null && metaValue !== "";
        });
    }, [contactsInSurvey, variable]);

    const variableType = useMemo(() => {
        if (contactsLoading) return VARIABLE_STATE.LOADING;
        if (!variableIsInUse) return VARIABLE_STATE.BROKEN;
        if (allStudentsHaveVariable) return VARIABLE_STATE.OK;
        return VARIABLE_STATE.MISSING;
    }, [contactsLoading, variableIsInUse, allStudentsHaveVariable]);

    const variableClass = CLASS_MAP[variableType];

    const variableTitle = intl.formatMessage({
        id: `${variableType}Title`,
        defaultMessage: DEFAULT_MESSAGES[variableType]
    });

    const variableDisplayName = intl.formatMessage({
        defaultMessage: variable,
        id: `contactmeta.${variable}`
    });

    return {
        variableClass,
        variableTitle,
        variableDisplayName,
        variableType,
        variableIsInUse,
        allStudentsHaveVariable,
        contactsInSurvey,
        contactMetaArray
    };
};